import {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {SupportChatApi} from './supportChatApi';
import {SupportChatService} from './SupportChatService';
import {SupportWebSocketsClient} from '../../websockets/WebSocketsClient';
import {SupportConversationThread} from './SupportConversationThread';
import {SupportComposer} from './SupportComposer';
import {SupportAvatar} from './SupportAvatar';
import {GREETING_TEAM_MEMBER} from './supportTeamIdentity';
import {Spinner} from '../Spinner';
import {isBusinessHours, relativeTimeParts} from './supportChatTime';

// SupportChatPanel — el panel de la burbuja de chat de soporte (Reemplazo de Intercom, Fase 3).
//
// Props:
//   agencyId: para suscribir el canal privado de realtime.
//   onClose(): cerrar el panel (delega en el bubble → SupportChatService.closePanel()).
//
// Tres vistas internas (state view): 'list' | 'new' | 'thread'.
//   - Al montar: si hay una conversación pendiente (`openConversation`: «Responder» de la tarjeta o
//     el link del mail) → arranca en ese hilo. Si no, si consumePrefill() devuelve texto → arranca
//     en 'new' con ese texto (semántica exacta de showNewMessage de Intercom: prellenar el composer
//     SIN enviar).
//   - Realtime: subscribeAgencyChannel al montar, unsubscribe SIEMPRE al desmontar. Si el WS no
//     está disponible → polling de fallback (25s) SOLO mientras el panel está abierto.

const FALLBACK_POLL_MS = 25000;
const PREVIEW_MAX = 140;

// {id, status} pendiente → la conversación activa del panel. Sin estado conocido arranca abierta y
// el GET del hilo lo confirma.
function pendingToActive(pending) {
    return {id: pending.id, status: pending.status || 'OPEN', imported_from_intercom: false};
}

export const SupportChatPanel = ({agencyId, onClose}) => {
    const {t} = useTranslation();

    // Prefill se consume UNA vez al montar (semántica del service). Si hay texto → vista 'new'.
    const initialPrefillRef = useRef(undefined);
    if (initialPrefillRef.current === undefined) {
        initialPrefillRef.current = SupportChatService.consumePrefill();
    }
    const hasPrefill = initialPrefillRef.current !== null && initialPrefillRef.current !== undefined;
    const initialConversationRef = useRef(undefined);
    if (initialConversationRef.current === undefined) {
        initialConversationRef.current = SupportChatService.consumePendingConversation();
    }
    const initialConversation = initialConversationRef.current;

    const [view, setView] = useState(initialConversation ? 'thread' : (hasPrefill ? 'new' : 'list'));
    // El texto del CTA para el editor de la conversación nueva ({text, id}). Va por prop y no como
    // borrador: el editor lee el borrador antes de que corran los efectos del panel (C-fserb-d0-01).
    const [seed, setSeed] = useState(
        hasPrefill && !initialConversation ? {text: initialPrefillRef.current || '', id: 1} : null);
    const [conversations, setConversations] = useState(null); // null = cargando
    // {id, status}. El estado real lo confirma el hilo al cargar (`onStatusChange`).
    const [activeConversation, setActiveConversation] = useState(
        initialConversation ? pendingToActive(initialConversation) : null);
    const [showClosed, setShowClosed] = useState(false);
    const [creating, setCreating] = useState(false);

    const threadRef = useRef(null);
    const activeConversationRef = useRef(null);
    const viewRef = useRef(view);
    const usingRealtimeRef = useRef(false);

    useEffect(() => { activeConversationRef.current = activeConversation; }, [activeConversation]);
    useEffect(() => { viewRef.current = view; }, [view]);

    // ── Fetch de la lista ────────────────────────────────────────────────────────
    const fetchList = useCallback(() => {
        SupportChatApi.fetchConversations().then((data) => {
            if (data && data.status === 'OK') {
                setConversations(data.conversations || []);
            } else {
                setConversations([]);
            }
        });
    }, []);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    // Con el panel YA abierto: `openConversation` («Responder» de otra tarjeta) lleva a ese hilo, y un
    // CTA con texto (`openWithMessage`) lleva a la conversación nueva con ese texto. Antes el panel
    // ignoraba el `prefill` y el clic no cambiaba nada (C-fserb-d0-01).
    useEffect(() => SupportChatService.subscribe((event) => {
        if (event.type === 'prefill') {
            const text = SupportChatService.consumePrefill();
            if (text === null) return;
            setSeed((prev) => ({text, id: (prev ? prev.id : 0) + 1}));
            setActiveConversation(null);
            setView('new');
            return;
        }
        if (event.type !== 'conversation') return;
        const pending = SupportChatService.consumePendingConversation();
        if (!pending) return;
        setActiveConversation(pendingToActive(pending));
        setView('thread');
    }), []);

    // ── Realtime + fallback polling (mientras el panel está montado) ─────────────
    useEffect(() => {
        const onSignal = (payload) => {
            const convId = payload && payload.conversation_id;
            const active = activeConversationRef.current;
            if (convId && active && String(convId) === String(active.id) && viewRef.current === 'thread') {
                // La señal es del hilo abierto → refetch incremental de ese hilo.
                if (threadRef.current) threadRef.current.refetchIncremental();
            } else {
                // Señal de otra conversación → refrescar la lista.
                fetchList();
            }
        };

        const subscribed = SupportWebSocketsClient.subscribeAgencyChannel(agencyId, onSignal);
        usingRealtimeRef.current = subscribed;

        let pollTimer = null;
        if (!subscribed) {
            // Sin realtime → polling de fallback SOLO mientras el panel está abierto.
            pollTimer = setInterval(() => {
                const active = activeConversationRef.current;
                if (active && viewRef.current === 'thread' && threadRef.current) {
                    threadRef.current.refetchIncremental();
                } else {
                    fetchList();
                }
            }, FALLBACK_POLL_MS);
        }

        return () => {
            if (pollTimer) clearInterval(pollTimer);
            // Desconectar SIEMPRE al cerrar/desmontar el panel (libera el slot de conexión).
            SupportWebSocketsClient.unsubscribeAgencyChannel();
        };
    }, [agencyId, fetchList]);

    // ── Navegación entre vistas ──────────────────────────────────────────────────
    const openThread = (conversation) => {
        setActiveConversation({
            id: conversation.id,
            status: conversation.status,
            imported_from_intercom: !!conversation.imported_from_intercom,
        });
        setView('thread');
        // Optimista: marcar la conv como leída en la lista local (el dot naranja se apaga).
        setConversations((prev) => (prev || []).map((c) => (
            c.id === conversation.id ? {...c, has_unread: false} : c
        )));
    };

    // El texto del CTA se usa una vez: al salir de la conversación nueva queda como borrador (lo
    // guardó el editor), y volver a entrar lo restaura de ahí, no del CTA.
    const backToList = () => {
        setSeed(null);
        setActiveConversation(null);
        setView('list');
        fetchList();
    };

    const startNewConversation = () => {
        setSeed(null);
        setView('new');
    };

    // ── Crear conversación nueva ─────────────────────────────────────────────────
    const handleCreate = async (message, attachmentBlob) => {
        setCreating(true);
        const response = await SupportChatApi.createConversation(message, attachmentBlob);
        setCreating(false);
        return response;
    };

    const handleCreated = (response) => {
        setSeed(null);
        SupportChatService.clearDraft('new');
        const conv = response.conversation;
        if (conv) {
            setActiveConversation({id: conv.id, status: conv.status || 'OPEN'});
            setView('thread');
        }
        fetchList();
    };

    const handleThreadStatusChange = (status) => {
        setActiveConversation((prev) => (prev ? {...prev, status} : prev));
    };

    // El hilo no se pudo cargar (el link del mail a una conversación que ya no es accesible, p. ej.
    // de otra cuenta): volver a la lista en vez de dejar un hilo vacío con el composer activo.
    const handleThreadLoadError = () => {
        backToList();
    };

    // ── Render del header (título + volver + cerrar) ─────────────────────────────
    const renderHeader = () => {
        const inThread = view === 'thread';
        const inNew = view === 'new';
        const showBack = inThread || inNew;
        let title = t('support_chat.panel_title');
        if (inNew) title = t('support_chat.new_conversation');
        return (
            <div className="support-chat-panel__header">
                {showBack && (
                    <button
                        type="button"
                        className="support-chat-panel__back"
                        onClick={backToList}
                        aria-label={t('support_chat.back')}
                    >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                    </button>
                )}
                <span className="support-chat-panel__title">{title}</span>
                <button
                    type="button"
                    className="support-chat-panel__close"
                    onClick={onClose}
                    aria-label={t('support_chat.close')}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                </button>
            </div>
        );
    };

    // ── Saludo / identidad (siempre arriba de la lista) ──────────────────────────
    // Una sola cara, la de quien contesta (`GREETING_TEAM_MEMBER`, con su foto); el texto habla del
    // equipo y no nombra a nadie (D10 del recorrido de la 2.1, 02/10).
    const renderGreeting = () => {
        const inHours = isBusinessHours();
        return (
            <div className="support-chat-greeting">
                <div className="support-chat-greeting__avatars">
                    <SupportAvatar name={GREETING_TEAM_MEMBER} size={36} team/>
                </div>
                <p className="support-chat-greeting__text">
                    {inHours ? t('support_chat.greeting_in_hours') : t('support_chat.greeting_off_hours')}
                </p>
            </div>
        );
    };

    // ── Item de la lista (estilo Intercom: avatar + nombre + preview + tiempo + dot) ──
    const renderConversationItem = (conv) => {
        const preview = (conv.last_message_preview || '').slice(0, PREVIEW_MAX);
        const rel = relativeTimeParts(conv.last_message_at || conv.updated_at);
        let relLabel = '';
        if (rel.kind === 'now') relLabel = t('support_chat.time_now');
        else if (rel.kind === 'minutes') relLabel = t('support_chat.time_minutes', {count: rel.value});
        else if (rel.kind === 'hours') relLabel = t('support_chat.time_hours', {count: rel.value});
        else if (rel.kind === 'yesterday') relLabel = t('support_chat.day_yesterday');
        else relLabel = rel.label;

        // Título de la fila = el último que habló. Si fue el equipo (ADMIN) reforzamos con
        // "· Dashbo" para que se lea la identidad humana; si fue el cliente, su nombre a secas.
        const isTeam = conv.last_message_author_type === 'ADMIN';
        const rawName = conv.last_message_author_name
            || (conv.opened_by && conv.opened_by.name)
            || t('support_chat.panel_title');
        const title = isTeam ? t('support_chat.team_name', {name: rawName, interpolation: {escapeValue: false}}) : rawName;
        // Chip "de <colega>" solo si la conversación la abrió otro usuario de mi agencia.
        const showFrom = conv.is_mine === false && conv.opened_by && conv.opened_by.name;

        return (
            <button
                type="button"
                key={conv.id}
                className={`support-chat-list__item ${conv.has_unread ? '-unread' : ''}`}
                onClick={() => openThread(conv)}
            >
                {/* La fila no tiene la foto REAL del último autor: el shape de conversación
                    (serialize_conversation_for_agency) sólo trae last_message_author_name/type. No
                    agregamos backend nuevo sólo para el avatar de la lista; la foto real del admin
                    aparece dentro del hilo (author_avatar_url por mensaje). Si el último que habló
                    es del equipo, la fila usa su foto del mapa estático (`TEAM_AVATAR_ASSETS`, por
                    primer nombre); si no, iniciales — `team` evita que alguien de la agencia con el
                    mismo nombre herede esa cara. */}
                <SupportAvatar name={rawName} size={40} team={isTeam}/>
                <span className="support-chat-list__item-body">
                    <span className="support-chat-list__item-top">
                        <span className="support-chat-list__item-name">{title}</span>
                        <span className="support-chat-list__item-time">{relLabel}</span>
                    </span>
                    <span className="support-chat-list__item-preview">
                        {preview || t('support_chat.no_messages_yet')}
                    </span>
                    {showFrom && (
                        <span className="support-chat-list__item-from">
                            {t('support_chat.from_name', {name: conv.opened_by.name, interpolation: {escapeValue: false}})}
                        </span>
                    )}
                </span>
                {conv.has_unread && <span className="support-chat-list__item-dot" aria-hidden="true"/>}
            </button>
        );
    };

    // ── Vista lista ──────────────────────────────────────────────────────────────
    const renderList = () => {
        if (conversations === null) {
            return (
                <div className="support-chat-panel__body">
                    {renderGreeting()}
                    <div className="box-loader"><Spinner color="positive"/></div>
                </div>
            );
        }

        const open = conversations.filter((c) => c.status !== 'CLOSED');
        const closed = conversations.filter((c) => c.status === 'CLOSED');

        return (
            <div className="support-chat-panel__body">
                {renderGreeting()}

                {open.length === 0 && closed.length === 0 && (
                    <div className="support-chat-list__empty">{t('support_chat.no_conversations')}</div>
                )}

                {open.length > 0 && (
                    <div className="support-chat-list">
                        {open.map(renderConversationItem)}
                    </div>
                )}

                {closed.length > 0 && (
                    <div className="support-chat-list__closed">
                        <button
                            type="button"
                            className="support-chat-list__closed-toggle"
                            onClick={() => setShowClosed((s) => !s)}
                            aria-expanded={showClosed}
                        >
                            <span>{t('support_chat.previous_conversations', {count: closed.length})}</span>
                            <svg
                                className={`support-chat-list__chevron ${showClosed ? '-open' : ''}`}
                                width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true"
                            >
                                <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>
                        {showClosed && (
                            <div className="support-chat-list -closed">
                                {closed.map(renderConversationItem)}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    // ── Footer con CTA "Nueva conversación" (solo en la lista) ────────────────────
    const renderListFooter = () => (
        <div className="support-chat-panel__footer">
            <button
                type="button"
                className="support-chat-panel__new-cta"
                onClick={startNewConversation}
            >
                {t('support_chat.new_conversation')}
            </button>
        </div>
    );

    // ── Vista nueva conversación ─────────────────────────────────────────────────
    const renderNew = () => (
        <div className="support-chat-panel__body -new">
            {renderGreeting()}
            <div className="support-chat-panel__new-composer">
                <SupportComposer
                    draftKey="new"
                    seed={seed}
                    disabled={creating}
                    onSend={handleCreate}
                    onSent={handleCreated}
                />
            </div>
        </div>
    );

    return (
        <div className="support-chat-panel" role="dialog" aria-label={t('support_chat.panel_title')}>
            <div className="support-chat-panel__rail" aria-hidden="true"/>
            {renderHeader()}

            {view === 'list' && renderList()}
            {view === 'new' && renderNew()}
            {view === 'thread' && activeConversation && (
                <div className="support-chat-panel__body -thread">
                    <SupportConversationThread
                        ref={threadRef}
                        conversationId={activeConversation.id}
                        conversationStatus={activeConversation.status}
                        conversationImported={activeConversation.imported_from_intercom}
                        onStatusChange={handleThreadStatusChange}
                        onLoadError={handleThreadLoadError}
                        onActivity={fetchList}
                        onStartNew={startNewConversation}
                    />
                </div>
            )}

            {view === 'list' && renderListFooter()}
        </div>
    );
};
